'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, createContext, useContext } from 'react';
import { VenueAddressActionWrapper } from '@/entities/venue/ui/venue-address-action';
import { VenueTelephoneActionWrapper } from '@/entities/venue/ui/venue-telephone-action';
import { QueryApi } from '@/features/query/api';
import { formatAddress } from '@/shared/lib/formatAddress';
import { getFormatedPhoneNumber } from '@/shared/lib/getFormatedPhoneNumber';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import './contact.scss';
import { SocialMediaPlatform } from './types';

const getSocialMediaIconName = (platform: SocialMediaPlatform) => {
    switch (platform) {
        case SocialMediaPlatform.FACEBOOK:
            return 'social-facebook' as const;
        case SocialMediaPlatform.INSTAGRAM:
            return 'social-instagram' as const;
        case SocialMediaPlatform.TIKTOK:
            return 'social-tiktok' as const;
    }
};

const getSocialMediaName = (platform: SocialMediaPlatform) => {
    switch (platform) {
        case SocialMediaPlatform.FACEBOOK:
            return 'Facebook';
        case SocialMediaPlatform.INSTAGRAM:
            return 'Instagram';
        case SocialMediaPlatform.TIKTOK:
            return 'TikTok';
    }
};

type ContactInfoBoxContextValue = {
    venueName?: string;
    formattedAddress?: string;
    venuePhoneNumber?: string;
    venuePhoneHref?: string;
    socialMediaLinks?: Array<{ platform: SocialMediaPlatform; url: string }>;
    translations?: {
        call?: string;
        navigate?: string;
        followUsOnSocials?: string;
    };
};

export const ContactInfoBoxContext = createContext<ContactInfoBoxContextValue>(
    {} as ContactInfoBoxContextValue,
);

type ContactInfoBoxWrapperProps = ComponentProps<typeof Layout>;

export const ContactInfoBoxWrapper = ({ children, ...props }: ContactInfoBoxWrapperProps) => {
    const t = useTranslations();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const venueData = venueQuery.data;

    const formattedAddress = venueData?.address ? formatAddress(venueData.address) : '';
    const { formated: venuePhoneNumber, href: venuePhoneHref } = getFormatedPhoneNumber(
        venueData?.contactInfo?.telephonePrefix || '',
        venueData?.contactInfo?.telephone || '',
    );

    const contextValue: ContactInfoBoxContextValue = {
        venueName: venueData?.name,
        formattedAddress,
        venuePhoneNumber,
        venuePhoneHref,
        socialMediaLinks: [], // Empty for now as per original code
        translations: {
            call: t('CONTACT.CONTACT_INFO.CALL'),
            navigate: t('CONTACT.CONTACT_INFO.NAVIGATE'),
            followUsOnSocials: t('CONTACT.CONTACT_INFO.FOLLOW_US_ON_SOCIALS'),
        },
    };

    return (
        <ContactInfoBoxContext.Provider value={contextValue}>
            <Layout className="card contact__box" {...props}>
                {children}
            </Layout>
        </ContactInfoBoxContext.Provider>
    );
};

type ContactInfoBoxTitleProps = ComponentProps<typeof Layout>;

export const ContactInfoBoxTitle = ({ media, ...props }: ContactInfoBoxTitleProps) => {
    const context = useContext(ContactInfoBoxContext);
    return (
        <Layout
            element="span"
            className="contact__box-title"
            media={media}
            data-testid="contact-info-box-venue-name"
            {...props}
        >
            {context.venueName}
        </Layout>
    );
};

type ContactInfoBoxActionsProps = ComponentProps<typeof Layout>;

export const ContactInfoBoxActions = ({ media, ...props }: ContactInfoBoxActionsProps) => {
    return (
        <Layout className="contact__info-box__actions" media={media} {...props}>
            <VenueTelephoneActionWrapper />
            <VenueAddressActionWrapper />
        </Layout>
    );
};

type ContactInfoBoxDividerProps = ComponentProps<typeof Layout>;

export const ContactInfoBoxDivider = ({ media, ...props }: ContactInfoBoxDividerProps) => {
    return <Layout element="div" className="contact__divider" media={media} {...props} />;
};

type ContactInfoBoxSocialTitleProps = ComponentProps<typeof Layout>;

export const ContactInfoBoxSocialTitle = ({ media, ...props }: ContactInfoBoxSocialTitleProps) => {
    const context = useContext(ContactInfoBoxContext);
    return (
        <Layout element="p" className="contact__info-box__social-title" media={media} {...props}>
            {context.translations?.followUsOnSocials}
        </Layout>
    );
};

type ContactInfoBoxSocialLinksProps = ComponentProps<typeof Layout> & {
    socialMediaLinks?: Array<{ platform: SocialMediaPlatform; url: string }>;
};

export const ContactInfoBoxSocialLinks = ({
    socialMediaLinks: linksProp,
    media,
    ...props
}: ContactInfoBoxSocialLinksProps) => {
    const context = useContext(ContactInfoBoxContext);
    const socialMediaLinks: Array<{ platform: SocialMediaPlatform; url: string }> =
        linksProp ?? context.socialMediaLinks ?? [];

    if (!socialMediaLinks.length) {
        return null;
    }

    return (
        <Layout className="contact__info-box__social-links" media={media} {...props}>
            {socialMediaLinks.map(({ platform, url }) => {
                const iconName = getSocialMediaIconName(platform);
                const name = getSocialMediaName(platform);
                return (
                    <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact__info-box__social-link"
                        data-platform={platform}
                    >
                        <Icon name={iconName} className="contact__info-box__social-icon" />
                        <span data-testid={`contact-info-box-social-${platform}`}>{name}</span>
                    </a>
                );
            })}
        </Layout>
    );
};

// Legacy export for backward compatibility
export const ContactInfoBox = ContactInfoBoxWrapper;

// Compound component structure
export const ContactInfoBoxCompound: typeof ContactInfoBoxWrapper & {
    Title: typeof ContactInfoBoxTitle;
    Actions: typeof ContactInfoBoxActions;
    Divider: typeof ContactInfoBoxDivider;
    SocialTitle: typeof ContactInfoBoxSocialTitle;
    SocialLinks: typeof ContactInfoBoxSocialLinks;
} = Object.assign(ContactInfoBoxWrapper, {
    Title: ContactInfoBoxTitle,
    Actions: ContactInfoBoxActions,
    Divider: ContactInfoBoxDivider,
    SocialTitle: ContactInfoBoxSocialTitle,
    SocialLinks: ContactInfoBoxSocialLinks,
});
